import { createModalFocusScope } from '../components/modal-focus-scope.js';
import { prefersReducedMotion } from '../components/motion-policy.js';

const POLL_INTERVAL_MS = 3000;
const POLL_SLOW_INTERVAL_MS = 8000;
const POLL_SLOW_AFTER_ATTEMPTS = 10;
const STATE_LEAVE_MS = 150;
// A single refused request is ordinary and stays silent; two in a row is a connection that is not
// coming back on its own, and on a phase that runs for minutes a screen frozen without explanation
// reads as an import that has died.
const POLL_ERROR_AFTER_FAILURES = 2;
// The dashboard is a page the reader is doing something else on, so the strip is re-read at a pace
// that keeps it honest rather than one that keeps it live.
const BANNER_POLL_INTERVAL_MS = 12000;

/**
 * The screens of the Steam import that poll: `Reading your Steam library`, `Matching your library` and
 * `Adding games to your library`.
 *
 * All three are queue deliveries, so the page which opened them is not driving them and has no event
 * to wait for. Polling replaces the `Check again` and `Refresh progress` links that used to sit there -
 * controls that reloaded the whole page and made the reader the polling mechanism.
 *
 * It requests the import page itself rather than a status endpoint. The screen it wants *is* that
 * page's answer, so a JSON endpoint would be a second copy of the state mapping and the card would
 * then have to be rebuilt here from it. What gets swapped in is the server's own markup, CSRF tokens
 * and all.
 *
 * Which states poll is read off the markup and never listed here: a state that renders
 * `data-steam-import-poll` asks to be polled, and everything else - a snapshot waiting for its owner,
 * a failure only a person can fix - is left alone. Automatic retries against a problem that does not
 * resolve itself are spent requests.
 */
export function initSteamImportStatus(
    documentRef = globalThis.document,
    fetchImpl = globalThis.fetch?.bind(globalThis),
    timerApi = globalThis,
    sectionRef = null,
    navigationRef = globalThis.location,
) {
    // A plain null check rather than `instanceof HTMLElement`: this runs on import, on every page, and
    // in an environment with no DOM globals at all that comparison is the thing that throws.
    const section = sectionRef ?? documentRef?.querySelector?.('[data-steam-import-poll]') ?? null;
    if (typeof fetchImpl !== 'function' || section === null) {
        return () => {};
    }

    if (!Object.prototype.hasOwnProperty.call(section.dataset, 'steamImportPoll')) {
        return () => {};
    }

    const statusUrl = String(section.dataset.steamImportStatusUrl ?? '').trim();
    const state = String(section.dataset.steamImportState ?? '');
    // Matching and the write each hold one state for minutes while their counts climb, so the state
    // alone is not enough to decide whether the server has anything new to show. Absent on every other
    // screen, where it reads as the empty string on both sides and changes nothing.
    const progress = String(section.dataset.steamImportProgress ?? '');
    if (statusUrl === '' || state === '') {
        return () => {};
    }

    // Rendered by the write alone, and absent everywhere else - the fallback is opt-in from the markup
    // exactly as the poll marker is.
    const pollError = section.querySelector?.('[data-steam-import-poll-error]') ?? null;
    const pollRetry = pollError?.querySelector?.('[data-steam-import-poll-retry]') ?? null;

    let attempts = 0;
    let failures = 0;
    let pending = false;
    let timeoutId = timerApi.setTimeout(poll, POLL_INTERVAL_MS);

    async function poll() {
        // One request in flight at a time. `Try again` calls this directly, so without the guard two
        // quick presses would leave two loops running - each scheduling its own timeout, the earlier id
        // overwritten and never cleared, and a late answer able to re-arm the module on a section that
        // has already been replaced.
        if (pending) {
            return;
        }

        pending = true;
        attempts += 1;

        try {
            const incoming = await fetchStateSection(documentRef, fetchImpl, statusUrl);
            failures = 0;
            setPollError(false);
            // A swap is worth it when the state changed, or when the same state carries a newer count.
            // Re-rendering an identical screen would restart the spinner on every tick and move focus
            // out of whatever the reader was on.
            const changedState = incoming instanceof HTMLElement
                && incoming.dataset.steamImportState !== state;
            const changedProgress = incoming instanceof HTMLElement
                && String(incoming.dataset.steamImportProgress ?? '') !== progress;

            // A swap while the stop confirmation is open, or while a stop is waiting for the batch in
            // flight, would take the dialog and its numbers off the screen mid-decision. The answer that
            // arrived is not lost - the comparison is against this render, so the next tick swaps it.
            if ((changedState || changedProgress) && !isHoldingSwap(section)) {
                // The screen that arrived decides for itself whether it goes on polling - a matching
                // card asks for the next tick, a review landing does not.
                const rearm = () => {
                    initSteamImportStatus(documentRef, fetchImpl, timerApi, incoming, navigationRef);
                    initSteamImportRefresh(documentRef, fetchImpl, timerApi, navigationRef, incoming);
                    initSteamImportStop(documentRef, incoming);
                };

                if (changedState) {
                    syncSteps(documentRef, incoming);
                    timeoutId = transitionSection(section, incoming, timerApi, rearm);
                } else {
                    // No leave animation for a count that moved: the screen is the same screen, and
                    // fading it out three times a minute is the jitter the transition exists to avoid.
                    section.replaceWith(incoming);
                    rearm();
                }

                return;
            }
        } catch (_error) {
            // A refused poll is not a failed import, and on the short phases it stays silent: the fetch
            // it watches has a backoff of its own and the server's next answer is the report. The write
            // renders the fallback below because it is the one phase long enough for a screen that has
            // stopped moving to read as a job that has died - and those two need opposite things from
            // the reader, so the screen has to say which it is.
            failures += 1;
            if (failures >= POLL_ERROR_AFTER_FAILURES) {
                setPollError(true);
            }
        } finally {
            pending = false;
        }

        timeoutId = timerApi.setTimeout(
            poll,
            attempts < POLL_SLOW_AFTER_ATTEMPTS ? POLL_INTERVAL_MS : POLL_SLOW_INTERVAL_MS,
        );
    }

    function setPollError(visible) {
        if (pollError !== null) {
            pollError.hidden = !visible;
        }
    }

    // `Try again` is the fallback's one control: it asks now instead of at the next tick. Polling never
    // stopped, so this shortens a wait rather than restarting anything.
    function retryNow() {
        timerApi.clearTimeout(timeoutId);
        attempts = 0;
        poll();
    }

    pollRetry?.addEventListener?.('click', retryNow);

    // ponytail: no pause on `document.hidden`. One timeout on one page, and navigating away discards
    // the document with it - add pausing if the request count matters.
    return () => {
        timerApi.clearTimeout(timeoutId);
        pollRetry?.removeEventListener?.('click', retryNow);
        section.classList.remove('is-leaving');
    };
}

/**
 * Whether the section is in the middle of something a swap would destroy: the stop confirmation open,
 * or a stop already submitted and waiting for the batch in flight.
 */
function isHoldingSwap(section) {
    const stopModal = section.querySelector?.('[data-steam-import-stop-modal]') ?? null;

    return (stopModal !== null && stopModal.hidden === false)
        || section.dataset?.steamImportStopping === 'true';
}

/**
 * Puts the confirmation in front of `Stop import`, and names what the press is doing while it waits.
 *
 * JS owns the confirmation step and never the transport: the dialog's button submits the same form
 * through `form=`, so with the modal open a submit is the confirmed one and with it closed it is the
 * trigger. Without this listener the form stops the import directly, as it always did.
 *
 * The pending label is not decoration. `POST /import/steam/cancel` waits up to ten seconds for the
 * batch in flight rather than announcing itself to it, and a control that sat there saying `Stop
 * import` through that wait invites a second press against a request that is working.
 */
export function initSteamImportStop(documentRef = globalThis.document, sectionRef = null) {
    const section = sectionRef ?? documentRef?.querySelector?.('[data-steam-import-state]') ?? null;
    const form = section?.querySelector?.('[data-steam-import-stop-form]') ?? null;
    const modal = section?.querySelector?.('[data-steam-import-stop-modal]') ?? null;
    const dialog = modal?.querySelector?.('[data-steam-import-stop-dialog]') ?? null;
    if (
        form === null
        || modal === null
        || dialog === null
        || typeof HTMLFormElement === 'undefined'
        || !(form instanceof HTMLFormElement)
    ) {
        return () => {};
    }

    const trigger = form.querySelector('[data-steam-import-stop-trigger]');
    const confirmButton = modal.querySelector('[data-steam-import-stop-confirm]');

    const closeModal = () => {
        if (modal.hidden) {
            return;
        }

        modal.hidden = true;
        focusScope.deactivate();
    };

    const focusScope = createModalFocusScope({ dialog, requestClose: closeModal, documentRef });
    if (focusScope === null) {
        return () => {};
    }

    const onSubmit = (event) => {
        if (modal.hidden) {
            event.preventDefault();
            modal.hidden = false;
            focusScope.activate(trigger);
            focusScope.focus();

            return;
        }

        // Confirmed, and the POST is on its way. The flag also holds the poller off, so the dialog does
        // not vanish under the wait it is reporting.
        section.dataset.steamImportStopping = 'true';
        if (confirmButton !== null) {
            confirmButton.disabled = true;
            confirmButton.textContent = 'Stopping import\u2026';
        }
    };

    form.addEventListener('submit', onSubmit);
    const closeButtons = Array.from(modal.querySelectorAll('[data-steam-import-stop-close]'));
    closeButtons.forEach((button) => button.addEventListener('click', closeModal));

    return () => {
        form.removeEventListener('submit', onSubmit);
        closeButtons.forEach((button) => button.removeEventListener('click', closeModal));
    };
}

/**
 * The dashboard strip, re-read slowly.
 *
 * The strip is what tells someone they may leave the import page, so a count frozen at load breaks the
 * one promise it makes. It is also not this page's subject: the interval is far slower than the import
 * screen's, and nothing about the update animates or moves focus - a number that flickered every dozen
 * seconds would pull the eye on a page the reader is using for something else.
 *
 * What comes back is the strip's own server-rendered markup from `/import/steam/banner`, built by the
 * same cheap view data the dashboard rendered from. So there is no second copy of "how far has the
 * import got" to disagree with the first, and an empty answer - the run ended and was seen, or was
 * dismissed elsewhere - simply takes the strip away.
 */
export function initSteamImportBanner(
    documentRef = globalThis.document,
    fetchImpl = globalThis.fetch?.bind(globalThis),
    timerApi = globalThis,
    bannerRef = null,
) {
    const banner = bannerRef ?? documentRef?.querySelector?.('[data-dashboard-steam-import]') ?? null;
    const bannerUrl = String(banner?.dataset?.dashboardSteamImportUrl ?? '').trim();
    if (typeof fetchImpl !== 'function' || banner === null || bannerUrl === '') {
        return () => {};
    }

    const token = String(banner.dataset.dashboardSteamImportToken ?? '');

    let timeoutId = timerApi.setTimeout(refresh, BANNER_POLL_INTERVAL_MS);

    async function refresh() {
        try {
            const response = await fetchImpl(bannerUrl, {
                headers: { 'Cache-Control': 'no-cache' },
                credentials: 'same-origin',
            });
            if (!response.ok) {
                throw new Error(`Steam import banner fetch failed with status ${response.status}`);
            }

            const template = documentRef.createElement('template');
            template.innerHTML = (await response.text()).trim();
            const incoming = template.content.querySelector('[data-dashboard-steam-import]');

            if (incoming === null) {
                // Nothing left to report: the run ended and its summary has been seen, or the strip was
                // dismissed from another tab.
                banner.remove();

                return;
            }

            // An unchanged answer changes nothing. Replacing the strip every twelve seconds would take
            // focus off `View progress` under anyone using the keyboard and re-insert a `role="status"`
            // that has nothing new to announce - on a page the reader is using for something else. The
            // markup cannot be compared directly, because the finished strip carries a CSRF token.
            if (String(incoming.dataset.dashboardSteamImportToken ?? '') === token) {
                timeoutId = timerApi.setTimeout(refresh, BANNER_POLL_INTERVAL_MS);

                return;
            }

            banner.replaceWith(incoming);
            initSteamImportBanner(documentRef, fetchImpl, timerApi, incoming);

            return;
        } catch (_error) {
            // Silent, and that is the difference between this and the import screen: the strip is not
            // the surface anyone is waiting on, and a warning about a failed background read would be
            // the loudest thing on a page about something else.
        }

        timeoutId = timerApi.setTimeout(refresh, BANNER_POLL_INTERVAL_MS);
    }

    return () => timerApi.clearTimeout(timeoutId);
}

/**
 * Enhances `Refresh from Steam` and `Check again`; without this listener the same form remains an
 * ordinary POST.
 * The mutation answers JSON, then the canonical GET supplies the same server-rendered state section
 * used by polling. A failed or uncertain POST is reconciled with that GET through navigation and is
 * never repeated automatically.
 */
export function initSteamImportRefresh(
    documentRef = globalThis.document,
    fetchImpl = globalThis.fetch?.bind(globalThis),
    timerApi = globalThis,
    navigationRef = globalThis.location,
    sectionRef = null,
) {
    const section = sectionRef ?? documentRef?.querySelector?.('[data-steam-import-state]') ?? null;
    const form = section?.querySelector?.('[data-steam-import-refresh]') ?? null;
    const statusUrl = String(section?.dataset?.steamImportStatusUrl ?? '').trim();
    if (
        form === null
        || typeof HTMLFormElement === 'undefined'
        || !(form instanceof HTMLFormElement)
        || typeof fetchImpl !== 'function'
        || statusUrl === ''
    ) {
        return () => {};
    }

    let pending = false;
    let transitionTimeoutId = null;
    const submitButton = form.querySelector('[type="submit"]');

    async function submitRefresh(event) {
        event.preventDefault();
        if (pending) {
            return;
        }

        pending = true;
        section.setAttribute('aria-busy', 'true');
        if (submitButton instanceof HTMLButtonElement) {
            submitButton.disabled = true;
        }

        try {
            const response = await fetchImpl(form.action, {
                method: 'POST',
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                body: new FormData(form),
            });
            const payload = await response.json();
            if (!response.ok || payload?.success !== true) {
                throw new Error('Steam library refresh was refused');
            }

            const incoming = await fetchStateSection(documentRef, fetchImpl, statusUrl);
            if (!(incoming instanceof HTMLElement) || incoming.dataset.steamImportState !== 'fetching') {
                throw new Error('Steam library refresh returned an unexpected state');
            }

            transitionTimeoutId = transitionSection(section, incoming, timerApi, () => {
                initSteamImportStatus(documentRef, fetchImpl, timerApi, incoming, navigationRef);
            });
        } catch (_error) {
            if (typeof navigationRef?.assign === 'function') {
                navigationRef.assign(statusUrl);
                return;
            }

            pending = false;
            section.removeAttribute('aria-busy');
            if (submitButton instanceof HTMLButtonElement) {
                submitButton.disabled = false;
            }
        }
    }

    form.addEventListener('submit', submitRefresh);

    return () => {
        form.removeEventListener('submit', submitRefresh);
        if (transitionTimeoutId !== null) {
            timerApi.clearTimeout(transitionTimeoutId);
        }
        section.classList.remove('is-leaving');
    };
}

/**
 * The step marker sits in the page intro, outside the swapped section, so a poll that carries the page
 * from `Importing` back to its review would otherwise leave `Import` highlighted over review content.
 * The marker shows where the owner is now, which the incoming section names.
 */
export function syncSteps(documentRef, incoming) {
    const current = incoming?.dataset?.steamImportStep;
    if (!current) {
        return;
    }

    const steps = documentRef?.querySelectorAll?.('.steam-import-steps [data-steam-import-step]') ?? [];
    for (const step of steps) {
        const isCurrent = step.dataset.steamImportStep === current;
        step.classList.toggle('is-current', isCurrent);
        if (isCurrent) {
            step.setAttribute('aria-current', 'step');
        } else {
            step.removeAttribute('aria-current');
        }
    }
}

async function fetchStateSection(documentRef, fetchImpl, statusUrl) {
    const response = await fetchImpl(statusUrl, {
        // No `X-Requested-With`: nothing on this route reads it, and the answer this wants is the
        // ordinary page. `no-cache` is the one header that changes anything here.
        headers: { 'Cache-Control': 'no-cache' },
        credentials: 'same-origin',
    });
    if (!response.ok) {
        throw new Error(`Steam import status fetch failed with status ${response.status}`);
    }

    const template = documentRef.createElement('template');
    template.innerHTML = (await response.text()).trim();
    const incoming = template.content.querySelector('[data-steam-import-state]');

    return incoming instanceof HTMLElement ? incoming : null;
}

function transitionSection(section, incoming, timerApi, onComplete) {
    const commit = () => {
        section.replaceWith(incoming);
        onComplete();
    };

    if (prefersReducedMotion(timerApi)) {
        commit();

        return null;
    }

    section.classList.add('is-leaving');

    return timerApi.setTimeout(commit, STATE_LEAVE_MS);
}

initSteamImportStatus();
initSteamImportRefresh();
initSteamImportStop();
initSteamImportBanner();
